import type { CadenceFrequency } from '@/types';
import { WeekDay } from '@/types';

/**
 * Single source of truth for the merged Scan Schedule summary sentence.
 *
 * Pure (no React, no hooks) so it is trivially unit-testable (FIDELITY I1).
 * Consumed identically by the collapsed card (§A5) and the wizard live
 * summary band (§C4). Templates are transcribed verbatim from the approved
 * mockup `<script>` (approved-mockup.html:348-413) and FIDELITY-CHECKLIST §E.
 *
 * Copy fidelity: en-dash `–` (U+2013) in the time window; middot `·` (U+00B7)
 * in the quarterly month list; literal ellipsis `…` in the pending sentence.
 */

export type { CadenceFrequency };

export interface ScanScheduleSummaryState {
  frequency: CadenceFrequency;
  days: WeekDay[]; // 0=Sun..6=Sat
  start: string; // "HH:MM"
  end: string; // "HH:MM"
}

/** A styled run of text. The renderer maps these flags to chariot tokens. */
export type SentenceBodyPart =
  | { text: string } // plain
  | { text: string; accent: true } // frequency phrase (brand-primary, bold)
  | { text: string; bold: true } // day list / "first {weekday}"
  | { text: string; mono: true }; // time window "HH:MM–HH:MM UTC"

const DOW_FULL = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

// freqLead — approved-mockup.html:348-350
export const freqLead = (f: CadenceFrequency): string =>
  ({
    weekly: 'every week',
    biweekly: 'every 2 weeks',
    monthly: 'every month',
    quarterly: 'every quarter',
  })[f];

// dayList — approved-mockup.html:351-359
// 1 -> "Mondays"; 2 -> "Mondays & Tuesdays"; 3+ -> "A, B & C"; all 7 -> "any day"
export function dayList(days: WeekDay[]): string {
  const sorted = [...days].sort((a, b) => a - b);
  if (sorted.length === 0) return '—';
  if (sorted.length === 7) return 'any day';
  const names = sorted.map(d => DOW_FULL[d]);
  if (names.length === 1) return names[0] + 's';
  if (names.length === 2) return names[0] + 's & ' + names[1] + 's';
  return (
    names
      .slice(0, -1)
      .map(n => n + 's')
      .join(', ') +
    ' & ' +
    names[names.length - 1] +
    's'
  );
}

// firstDay — approved-mockup.html:360-363 (earliest selected weekday full name)
export function firstDay(days: WeekDay[]): string {
  const sorted = [...days].sort((a, b) => a - b);
  return sorted.length ? DOW_FULL[sorted[0]] : '—';
}

// timeWindow — approved-mockup.html:364 (en-dash U+2013 + " UTC")
export function timeWindow(start: string, end: string): string {
  return start + '–' + end + ' UTC';
}

/**
 * The four summary-sentence templates (FIDELITY §E, mockup `sentenceHTML`
 * lines 377-387). Returns ordered styled runs — NOT an HTML string — so the
 * renderer can apply chariot tokens and React stays XSS-safe.
 *
 * - E1 weekly:    Scan every week, on {days}, between {start}–{end} UTC.
 * - E2 biweekly:  Scan every 2 weeks, on the {days} window, between {win}.
 * - E3 monthly:   Scan every month, on the first {firstDay} window, between {win}.
 * - E4 quarterly: Scan every quarter (Jan · Apr · Jul · Oct), on the first
 *                 {firstDay} window, between {win}.
 */
export function buildSentence(s: ScanScheduleSummaryState): SentenceBodyPart[] {
  const win: SentenceBodyPart = {
    text: timeWindow(s.start, s.end),
    mono: true,
  };

  if (s.frequency === 'weekly') {
    return [
      { text: 'Scan ' },
      { text: 'every week', accent: true },
      { text: ', on ' },
      { text: dayList(s.days), bold: true },
      { text: ', between ' },
      win,
      { text: '.' },
    ];
  }
  if (s.frequency === 'biweekly') {
    return [
      { text: 'Scan ' },
      { text: 'every 2 weeks', accent: true },
      { text: ', on the ' },
      { text: dayList(s.days), bold: true },
      { text: ' window, between ' },
      win,
      { text: '.' },
    ];
  }
  if (s.frequency === 'monthly') {
    return [
      { text: 'Scan ' },
      { text: 'every month', accent: true },
      { text: ', on the ' },
      { text: 'first ' + firstDay(s.days), bold: true },
      { text: ' window, between ' },
      win,
      { text: '.' },
    ];
  }
  // quarterly
  return [
    { text: 'Scan ' },
    { text: 'every quarter', accent: true },
    { text: ' (Jan · Apr · Jul · Oct), on the ' },
    { text: 'first ' + firstDay(s.days), bold: true },
    { text: ' window, between ' },
    win,
    { text: '.' },
  ];
}

/**
 * E5 pending state (no day selected in the wizard yet): the band shows only the
 * frequency phrase `Scan {freqLead}` — no trailing em-dash and no instruction.
 * `pending` is empty so the band can skip rendering the italic trailing run.
 */
export interface PendingSentence {
  lead: SentenceBodyPart[]; // "Scan {freqLead}"
  pending: string; // empty (no instruction copy)
}

export function buildPending(f: CadenceFrequency): PendingSentence {
  return {
    lead: [{ text: 'Scan ' }, { text: freqLead(f), accent: true }],
    pending: '',
  };
}

/**
 * A7 unset state (no `scan_cadence_base` / `schedule` set):
 * `No schedule set — Default: scan daily at 00:00 UTC (all days, all hours).`
 */
export const DEFAULT_SENTENCE: SentenceBodyPart[] = [
  { text: 'No schedule set — ' },
  { text: 'Default', bold: true },
  { text: ': scan ' },
  { text: 'daily', accent: true },
  { text: ' at ' },
  { text: '00:00 UTC', mono: true },
  { text: ' (all days, all hours).' },
];
